// components/StatusModal.jsx
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DocStatusBadge } from '../common/DocStatusBadge';

/* ── Small reusable pieces matching WeeklyScheduleModal ── */
const SectionLabel = ({ children }) => (
  <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
    <span className="w-[3px] h-4 rounded-full bg-gradient-to-b from-indigo-500 to-violet-600 flex-shrink-0" />
    {children}
  </h3>
);

/* Each key-value row inside the info card */
const InfoRow = ({ label, value, last = false }) => (
  <div className={`flex items-center justify-between gap-4 py-2.5 ${!last ? 'border-b border-gray-100' : ''}`}>
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">{label}</p>
    <p className="text-sm font-semibold text-gray-800 text-left truncate max-w-[55%]">{value || '—'}</p>
  </div>
);

/* ── Main component ── */
const StatusModal = ({ document, onClose }) => {
  const isTaxDoc = useMemo(() => document?.type === 'תיאום מס', [document]);

  const dateLabel = isTaxDoc ? 'תאריך קליטה / תחילת תוקף' : 'תאריך העלאה';
  const hasNotes  = !!String(document?.notes || '').trim();

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      dir="rtl"
      onMouseDown={onClose}
    >
      {/* Backdrop — matches WeeklyScheduleModal exactly */}
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" />

      {/* Modal shell — sheet on mobile, centered card on sm+ */}
      <div
        className="relative z-[1] w-full sm:max-w-lg bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/25
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh]
                   mx-0 sm:mx-4"
        onMouseDown={(e) => e.stopPropagation()}
      >

        {/* ── Header — matches WeeklyScheduleModal header ── */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 rounded-t-3xl px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            {/* Icon badge */}
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">פרטי מסמך</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">{document?.type || 'מסמך'}</p>
            </div>
          </div>

          {/* Close button — absolute, same position as WeeklyScheduleModal */}
          <button
            type="button"
            onClick={onClose}
            aria-label="סגור"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-5">

          {/* Document details card */}
          <div>
            <div className="mb-3">
              <SectionLabel>פרטי המסמך</SectionLabel>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-gray-50/60 px-4 py-1">
              <InfoRow label="שם המסמך"  value={document?.customName} />
              <InfoRow label="שם הקובץ"  value={document?.fileName} />
              <InfoRow label="סוג מסמך"  value={document?.type} />
              <InfoRow label={dateLabel}  value={document?.date} />

              {isTaxDoc && (
                <>
                  <InfoRow
                    label="אחוז ניקוי מס"
                    value={
                      document?.taxPercent === null || document?.taxPercent === undefined
                        ? '—'
                        : `${document.taxPercent}%`
                    }
                  />
                  <InfoRow label="תוקף עד (פקיעה)" value={document?.taxValidTo} />
                </>
              )}

              {/* Status row — uses DocStatusBadge, value aligned left */}
              <div className="flex items-center justify-between gap-4 py-2.5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">סטטוס</p>
                <DocStatusBadge status={document?.status} />
              </div>
            </div>
          </div>

          {/* Notes section */}
          {hasNotes && (
            <div>
              <div className="mb-3">
                <SectionLabel>הערות מנהל</SectionLabel>
              </div>

              <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-yellow-50 to-amber-50 px-4 py-3 border-r-4 border-r-yellow-400">
                <p className="whitespace-pre-wrap text-gray-800 text-sm leading-relaxed break-words">
                  {document.notes}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer — matches WeeklyScheduleModal footer ── */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                       shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                       transition-all duration-150 flex items-center gap-2"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            סגור
          </button>
        </div>

      </div>
    </div>,
    window.document.body
  );
};

export default StatusModal;